import React, { useState, useRef, useCallback } from 'react';
import { Drain } from '../types';

interface DrainEditDialogProps {
  drain: Drain;
  horizontal: number;
  vertical: number;
  panelThick: number;
  onConfirm: (drain: Drain) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
}

const DIALOG_W = 340;

const DrainEditDialog: React.FC<DrainEditDialogProps> = ({
  drain, horizontal, vertical, panelThick, onConfirm, onDelete, onCancel,
}) => {
  // 패널 안쪽 면 기준 거리 계산
  // 좌측 안쪽 = panelThick, 우측 안쪽 = horizontal - panelThick
  // 상단 안쪽 = panelThick, 하단 안쪽 = vertical - panelThick
  const distFromLeft   = Math.round(drain.x - panelThick);
  const distFromRight  = Math.round((horizontal - panelThick) - drain.x);
  const distFromTop    = Math.round(drain.y - panelThick);
  const distFromBottom = Math.round((vertical - panelThick) - drain.y);

  const initDistH = distFromLeft <= distFromRight
    ? { side: 'left' as const,  val: distFromLeft }
    : { side: 'right' as const, val: distFromRight };
  const initDistV = distFromTop <= distFromBottom
    ? { side: 'top' as const,    val: distFromTop }
    : { side: 'bottom' as const, val: distFromBottom };

  const [hSide, setHSide] = useState<'left' | 'right'>(initDistH.side);
  const [hVal,  setHVal]  = useState(initDistH.val);
  const [vSide, setVSide] = useState<'top' | 'bottom'>(initDistV.side);
  const [vVal,  setVVal]  = useState(initDistV.val);
  const [size, setSize]   = useState(drain.size);
  const [direction, setDirection] = useState(drain.direction);

  const [pos, setPos] = useState({ x: 24, y: window.innerHeight - 520 - 80 });
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    e.preventDefault();
  }, [pos]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setPos({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  const handleConfirm = () => {
    // 패널 안쪽 면 기준 → 실제 좌표로 역산
    const newX = hSide === 'left'
      ? panelThick + hVal
      : (horizontal - panelThick) - hVal;
    const newY = vSide === 'top'
      ? panelThick + vVal
      : (vertical - panelThick) - vVal;
    if (newX < 0 || newX > horizontal || newY < 0 || newY > vertical) {
      alert('배수구 위치가 도면을 벗어납니다.');
      return;
    }
    onConfirm({ ...drain, x: Math.round(newX), y: Math.round(newY), size, direction });
  };

  const sideBtn = (label: string, active: boolean, onClick: () => void) => (
    <button onClick={onClick} style={{
      padding: '4px 10px', fontSize: 11, cursor: 'pointer', borderRadius: 8,
      border: active ? '2px solid #3182F6' : '1px solid #E5E8EB',
      background: active ? '#E8F3FF' : '#fff',
      color: active ? '#3182F6' : '#666', fontWeight: active ? 'bold' : 'normal',
    }}>{label}</button>
  );

  return (
    <div
      style={{
        position: 'fixed', left: pos.x, top: pos.y, width: DIALOG_W, zIndex: 1000,
        background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.16)', overflow: 'hidden', userSelect: 'none',
      }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <div onMouseDown={onMouseDown} style={{
        background: '#fff', color: '#191F28', padding: '16px 20px 8px',
        borderRadius: 0, cursor: 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>배수구 수정</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>

      <div style={{ padding: 16 }}>

        {/* 가로 거리 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>가로 거리 (mm)</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {sideBtn('좌측 벽', hSide === 'left',  () => setHSide('left'))}
            {sideBtn('우측 벽', hSide === 'right', () => setHSide('right'))}
            <input type="number" value={hVal} onChange={e => setHVal(Number(e.target.value))}
              style={{ ...dinputStyle, flex: 1 }} step={10} />
          </div>
          <span style={hintStyle}>
            좌측 패널 안쪽: {Math.round(hSide === 'left' ? hVal : (horizontal - panelThick * 2) - hVal)}mm /
            우측 패널 안쪽: {Math.round(hSide === 'right' ? hVal : (horizontal - panelThick * 2) - hVal)}mm
          </span>
        </div>

        {/* 세로 거리 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>세로 거리 (mm)</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {sideBtn('상단 벽', vSide === 'top',    () => setVSide('top'))}
            {sideBtn('하단 벽', vSide === 'bottom', () => setVSide('bottom'))}
            <input type="number" value={vVal} onChange={e => setVVal(Number(e.target.value))}
              style={{ ...dinputStyle, flex: 1 }} step={10} />
          </div>
          <span style={hintStyle}>
            상단 패널 안쪽: {Math.round(vSide === 'top' ? vVal : (vertical - panelThick * 2) - vVal)}mm /
            하단 패널 안쪽: {Math.round(vSide === 'bottom' ? vVal : (vertical - panelThick * 2) - vVal)}mm
          </span>
        </div>

        {/* 크기 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>배수구 크기 (mm)</label>
          <input type="number" value={size} onChange={e => setSize(Number(e.target.value))}
            style={dinputStyle} step={10} />
        </div>

        {/* 방향 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>배수 방향</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {([['up','↑ 위'],['down','↓ 아래'],['left','← 왼쪽'],['right','→ 오른쪽']] as const).map(([dir, label]) => (
              <button key={dir} onClick={() => setDirection(dir)}
                style={{
                  flex: 1, padding: '6px 4px', fontSize: 12,
                  border: direction === dir ? '2px solid #3182F6' : '1px solid #E5E8EB',
                  borderRadius: 8, background: direction === dir ? '#E8F3FF' : '#fff',
                  cursor: 'pointer',
                }}>{label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
          <button onClick={() => {
            if (window.confirm('배수구를 삭제하시겠습니까?')) onDelete(drain.id);
          }} style={deleteBtnStyle}>삭제</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={cancelBtnStyle}>취소</button>
            <button onClick={handleConfirm} style={confirmBtnStyle}>적용</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const fieldStyle: React.CSSProperties = { marginBottom: 12 };
const dlabelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' };
const dinputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, boxSizing: 'border-box' };
const hintStyle: React.CSSProperties = { fontSize: 11, color: '#999', marginTop: 3, display: 'block' };
const deleteBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#FFEEF0', color: '#F04452', cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#F2F4F6', color: '#333D4B', cursor: 'pointer' };
const confirmBtnStyle: React.CSSProperties = { height: 38, padding: '0 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#3182F6', color: '#fff', cursor: 'pointer' };

export default DrainEditDialog;
